import { h } from 'preact'
import { useEffect, useRef, useState } from 'preact/hooks'
import PropTypes from 'prop-types'

import classNames from 'lib/classNames'
import { useTurnstileConfig } from 'resources/captcha'

import Loading from 'components/Loading'
import ErrorMessage from 'components/ErrorMessage'
import './index.sass'

// Load Turnstile script
const loadTurnstileScript = () => {
  return new Promise((resolve, reject) => {
    if (window.turnstile) {
      resolve(window.turnstile)
      return
    }

    const script = document.createElement('script')
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js'
    script.async = true
    script.defer = true
    script.onload = () => {
      if (window.turnstile) {
        resolve(window.turnstile)
      } else {
        reject(new Error('Turnstile script loaded but API not available'))
      }
    }
    script.onerror = () => reject(new Error('Failed to load Turnstile script'))

    document.head.appendChild(script)
  })
}

export default function Turnstile({
  className,
  onVerify,
  onError,
  onExpire,
  onTimeout,
  disabled,
  theme = 'auto',
  size = 'normal',
  ...props
}){
  const containerRef = useRef()
  const widgetId = useRef(null)
  const [scriptLoaded, setScriptLoaded] = useState(false)
  const [scriptError, setScriptError] = useState(null)
  const { turnstileConfig, loading, error } = useTurnstileConfig()

  // Use refs to store the latest callback functions to avoid re-rendering
  const onVerifyRef = useRef(onVerify)
  const onErrorRef = useRef(onError)
  const onExpireRef = useRef(onExpire)
  const onTimeoutRef = useRef(onTimeout)

  // Update refs when callbacks change
  useEffect(() => {
    onVerifyRef.current = onVerify
    onErrorRef.current = onError
    onExpireRef.current = onExpire
    onTimeoutRef.current = onTimeout
  }, [onVerify, onError, onExpire, onTimeout])

  // Load Turnstile script
  useEffect(() => {
    loadTurnstileScript()
      .then(() => setScriptLoaded(true))
      .catch((err) => setScriptError(err))
  }, [])

  // Render Turnstile widget
  useEffect(() => {
    if (!scriptLoaded || !turnstileConfig || !containerRef.current || loading) {
      return
    }

    // Clear any existing widget
    if (widgetId.current !== null) {
      try {
        window.turnstile.remove(widgetId.current)
      } catch (e) {
        // Widget may already be removed
      }
      widgetId.current = null
    }

    // Render new widget
    try {
      widgetId.current = window.turnstile.render(containerRef.current, {
        sitekey: turnstileConfig.siteKey,
        theme: theme,
        size: size,
        callback: (token) => {
          if (onVerifyRef.current) {
            onVerifyRef.current(token)
          }
        },
        'error-callback': (error) => {
          if (onErrorRef.current) {
            onErrorRef.current(error)
          }
        },
        'expired-callback': () => {
          if (onExpireRef.current) {
            onExpireRef.current()
          }
        },
        'timeout-callback': () => {
          if (onTimeoutRef.current) {
            onTimeoutRef.current()
          }
        }
      })
    } catch (err) {
      setScriptError(err)
    }

    // Cleanup function
    return () => {
      if (widgetId.current !== null) {
        try {
          window.turnstile.remove(widgetId.current)
        } catch (e) {
          // Widget may already be removed
        }
        widgetId.current = null
      }
    }
  }, [scriptLoaded, turnstileConfig, theme, size, loading])

  // Reset widget when disabled state changes
  useEffect(() => {
    if (scriptLoaded && widgetId.current !== null && disabled !== undefined) {
      try {
        if (disabled) {
          // Reset the widget when disabled
          window.turnstile.reset(widgetId.current)
        }
      } catch (e) {
        // Ignore errors during reset
      }
    }
  }, [disabled, scriptLoaded])

  if (loading) {
    return <Loading size="sm" type="block"/>
  }

  if (error || scriptError) {
    return <ErrorMessage error={error || scriptError}/>
  }

  if (!turnstileConfig || !turnstileConfig.siteKey) {
    return <ErrorMessage error={new Error('Turnstile configuration not available')}/>
  }

  return <div
    {...props}
    className={classNames('Turnstile', { className })}
  >
    <div ref={containerRef} className="Turnstile-widget" />
  </div>
}

Turnstile.propTypes = {
  className: PropTypes.string,
  onVerify: PropTypes.func.isRequired,
  onError: PropTypes.func,
  onExpire: PropTypes.func,
  onTimeout: PropTypes.func,
  disabled: PropTypes.bool,
  theme: PropTypes.oneOf(['auto', 'light', 'dark']),
  size: PropTypes.oneOf(['normal', 'compact']),
}

// Legacy component name for backward compatibility
export { Turnstile as Captcha }
